import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "What services can I book?",
  "Tell me about DermaGen balm",
  "Can a pharmacist treat an ear infection?",
  "What are your opening hours?",
];

const Orb = ({ onClick }) => (
  <motion.button
    onClick={onClick}
    data-testid="orb-assistant-toggle"
    aria-label="Open AI assistant"
    className="relative flex h-16 w-16 items-center justify-center"
    whileHover={{ scale: 1.08 }}
    whileTap={{ scale: 0.94 }}
  >
    <span className="orb-ring absolute inset-0 rounded-full bg-brand-pink/30" />
    <span className="orb-ring absolute inset-0 rounded-full bg-brand-green/25" style={{ animationDelay: "1.6s" }} />
    <span className="orb-core absolute inset-1 rounded-full bg-gradient-to-br from-brand-pink via-[#c7368f] to-brand-green shadow-[0_8px_32px_rgba(229,0,126,0.45)]" />
    <span className="absolute left-3 top-2.5 h-4 w-4 rounded-full bg-white/50 blur-[3px]" />
    <Sparkles className="relative h-6 w-6 text-white drop-shadow" />
  </motion.button>
);

export default function OrbAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi, I'm Genie — the Priceline Pharmacy Margaret River assistant. Ask me about our services, bookings or DermaGen skincare." },
  ]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const scrollRef = useRef(null);
  const sessionRef = useRef(null);

  if (!sessionRef.current && typeof window !== "undefined") {
    sessionRef.current = localStorage.getItem("genie_session") || crypto.randomUUID();
    localStorage.setItem("genie_session", sessionRef.current);
  }

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, thinking]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || thinking) return;
    setInput("");
    setThinking(true);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message }),
      });
      if (!res.ok) throw new Error("chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let done = false;
      while (!done) {
        const { value, done: streamDone } = await reader.read();
        if (streamDone) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = part.slice(6).trim();
          if (payload === "[DONE]") { done = true; break; }
          try {
            const obj = JSON.parse(payload);
            if (obj.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + obj.delta };
                return copy;
              });
            } else if (obj.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: obj.error };
                return copy;
              });
            }
          } catch { /* partial json */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I'm having trouble right now. Please call us on (08) 9757 2224 and we'll be happy to help." };
        return copy;
      });
    } finally {
      setThinking(false);
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-[100] flex flex-col items-end gap-4" data-testid="orb-assistant">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.95 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="flex h-[540px] w-[min(380px,calc(100vw-40px))] flex-col overflow-hidden rounded-3xl border border-border/60 bg-white shadow-[0_24px_64px_rgba(0,0,0,0.18)]"
            data-testid="orb-chat-panel"
          >
            <div className="flex items-center justify-between bg-gradient-to-r from-brand-pink to-brand-pinkdark px-5 py-4 text-white">
              <div className="flex items-center gap-3">
                <span className="orb-core flex h-9 w-9 items-center justify-center rounded-full bg-white/20">
                  <Sparkles className="h-4 w-4" />
                </span>
                <div>
                  <p className="font-heading text-sm font-bold leading-tight">Genie</p>
                  <p className="text-xs text-white/75">Pharmacy assistant · answers instantly</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="orb-chat-close" aria-label="Close chat" className="rounded-full p-1.5 transition-colors duration-200 hover:bg-white/20">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div ref={scrollRef} className="chat-scroll flex-1 space-y-3 overflow-y-auto bg-brand-cream/60 px-4 py-4" data-testid="orb-chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-message-${i}`}
                    className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "rounded-br-sm bg-brand-pink text-white"
                        : "rounded-bl-sm border border-border/60 bg-white text-foreground shadow-sm"
                    }`}
                  >
                    {m.content || (thinking && i === messages.length - 1 ? (
                      <span className="flex gap-1 py-1">
                        <span className="typing-dot h-1.5 w-1.5 rounded-full bg-brand-pink" />
                        <span className="typing-dot h-1.5 w-1.5 rounded-full bg-brand-pink" style={{ animationDelay: "0.15s" }} />
                        <span className="typing-dot h-1.5 w-1.5 rounded-full bg-brand-pink" style={{ animationDelay: "0.3s" }} />
                      </span>
                    ) : "")}
                  </div>
                </div>
              ))}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-2" data-testid="orb-chat-suggestions">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid={`suggestion-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => send(s)}
                      className="rounded-full border border-brand-pink/30 bg-white px-3 py-1.5 text-xs font-medium text-brand-pink transition-colors duration-200 hover:bg-brand-blush"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-2 border-t border-border/60 bg-white p-3"
              data-testid="orb-chat-form"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about services or DermaGen…"
                data-testid="orb-chat-input"
                className="h-10 flex-1 rounded-full border border-input bg-background px-4 text-sm focus:outline-none focus:ring-2 focus:ring-brand-pink/40"
              />
              <button
                type="submit"
                disabled={thinking || !input.trim()}
                data-testid="orb-chat-send"
                aria-label="Send message"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-pink text-white transition-colors duration-200 hover:bg-brand-pinkdark disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
      {!open && <Orb onClick={() => setOpen(true)} />}
      {open && (
        <motion.button
          onClick={() => setOpen(false)}
          data-testid="orb-assistant-collapse"
          aria-label="Close AI assistant"
          className="flex h-12 w-12 items-center justify-center rounded-full bg-foreground text-white shadow-lg transition-colors duration-200 hover:bg-black"
          whileHover={{ scale: 1.06 }}
        >
          <X className="h-5 w-5" />
        </motion.button>
      )}
    </div>
  );
}
